/** Client entity + owner graph for Create Application drawer. */

import { isRealPersistedClient, resolveSelectedClientId } from '../../../services/funding/fundingClientsService';

export const ENTITY_TYPE_OPTIONS = [
  'Personal Profile',
  'LLC',
  'DBA',
  'Co-owner Profile',
  'Holding Company',
  'Business 2',
  'Other business entity',
];

export const ENTITY_STATUS_OPTIONS = [
  'Draft',
  'Documents Pending',
  'Ready for Analysis',
  'Building',
  'Submitted',
  'Funded',
];

export const OWNER_ROLE_OPTIONS = [
  'Primary Owner',
  'Co-owner',
  'Guarantor',
  'Spouse',
  'Officer',
];

export const ROLE_ON_APPLICATION_OPTIONS = [
  'Primary Applicant',
  'Co-applicant',
  'Co-applicant / Guarantor',
  'Guarantor',
  'Authorized User',
  'Spouse',
  'Officer',
];

const OWNER_BADGE_MAP = {
  'Primary Owner': 'Primary',
  'Co-owner': 'Co-owner',
  Guarantor: 'Guarantor',
  Spouse: 'Spouse',
  Officer: 'Officer',
};

export function warnSampleLeakBlocked(client, source) {
  if (process.env.NODE_ENV === 'production') return;
  console.warn('[FundGenius Sample Leak Blocked]', {
    clientId: resolveSelectedClientId(client),
    source,
  });
}

export function formatOwnerSensitiveValue(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  return String(value);
}

export function ownerBadgeLabel(role) {
  return OWNER_BADGE_MAP[role] || role || 'Owner';
}

export function entitySelectLabel(entity) {
  if (!entity) return '—';
  const name = entity.businessLegalName || entity.dbaName || entity.displayName || entity.label;
  const suffix = entity.label ? ` · ${entity.label}` : '';
  return `${name}${suffix}`;
}

function getSampleClientEntities(client) {
  const intake = client?.intake || {};
  const businessName = client?.businessName || client?.legalBusinessName || intake.businessLegalName || 'MS Beauty Studio LLC';

  return [
    {
      id: 'entity_ms_beauty',
      label: 'Primary',
      entityType: 'LLC',
      businessLegalName: businessName,
      dbaName: '',
      einMasked: intake.einMasked || '**-***1931',
      businessStartDate: intake.businessStartDate || '2021-03-13',
      businessAddress: intake.businessAddress || '44 Market Plaza, Orlando, FL',
      status: client?.status || 'Ready for Analysis',
      owners: [
        {
          id: 'owner_marie',
          name: client?.name || 'Marie Samuelle',
          role: 'Primary Owner',
          ownershipPercent: 70,
          ssnMasked: intake.ssnMasked || '***-**-6789',
          dob: intake.dob || '1988-11-05',
          personalAddress: intake.personalAddress || '211 Palm Ridge Dr, Orlando, FL',
          income: intake.personalIncome || 92000,
          employer: intake.employer || 'Self-employed',
          isPrimary: true,
        },
        {
          id: 'owner_jean',
          name: 'Jean Samuelle',
          role: 'Co-owner',
          ownershipPercent: 30,
          ssnMasked: '***-**-4421',
          dob: '1987-02-14',
          personalAddress: intake.personalAddress || '211 Palm Ridge Dr, Orlando, FL',
          income: 78000,
          employer: 'Self-employed',
          isPrimary: false,
        },
      ],
    },
    {
      id: 'entity_marie_personal',
      label: 'Personal Profile',
      entityType: 'Individual',
      businessLegalName: client?.name || 'Marie Samuelle',
      dbaName: '',
      einMasked: '',
      businessStartDate: '',
      businessAddress: '',
      status: client?.status || 'Ready for Analysis',
      owners: [
        {
          id: 'owner_marie_personal',
          name: client?.name || 'Marie Samuelle',
          role: 'Primary Owner',
          ownershipPercent: 100,
          ssnMasked: intake.ssnMasked || '***-**-6789',
          dob: intake.dob || '1988-11-05',
          personalAddress: intake.personalAddress || '211 Palm Ridge Dr, Orlando, FL',
          income: intake.personalIncome || 92000,
          employer: intake.employer || 'Self-employed',
          isPrimary: true,
        },
      ],
    },
    {
      id: 'entity_ms_holdings',
      label: 'Business 2',
      entityType: 'LLC',
      businessLegalName: 'MS Holdings LLC',
      dbaName: '',
      einMasked: '**-***2244',
      businessStartDate: '2019-06-01',
      businessAddress: '44 Market Plaza, Orlando, FL',
      status: client?.status || 'Ready for Analysis',
      owners: [
        {
          id: 'owner_marie_holdings',
          name: client?.name || 'Marie Samuelle',
          role: 'Officer',
          ownershipPercent: 100,
          ssnMasked: intake.ssnMasked || '***-**-6789',
          dob: intake.dob || '1988-11-05',
          personalAddress: intake.personalAddress || '211 Palm Ridge Dr, Orlando, FL',
          income: intake.personalIncome || 92000,
          employer: intake.employer || 'Self-employed',
          isPrimary: true,
        },
      ],
    },
    {
      id: 'entity_ms_dba',
      label: 'DBA',
      entityType: 'DBA',
      businessLegalName: businessName,
      dbaName: 'MS Beauty DBA',
      einMasked: intake.einMasked || '**-***1931',
      businessStartDate: intake.businessStartDate || '2021-03-13',
      businessAddress: intake.businessAddress || '44 Market Plaza, Orlando, FL',
      status: client?.status || 'Ready for Analysis',
      owners: [
        {
          id: 'owner_marie_dba',
          name: client?.name || 'Marie Samuelle',
          role: 'Primary Owner',
          ownershipPercent: 100,
          ssnMasked: intake.ssnMasked || '***-**-6789',
          dob: intake.dob || '1988-11-05',
          personalAddress: intake.personalAddress || '211 Palm Ridge Dr, Orlando, FL',
          income: intake.personalIncome || 92000,
          employer: intake.employer || 'Self-employed',
          isPrimary: true,
        },
      ],
    },
  ];
}

export function getClientEntities(client) {
  if (client?.entities?.length) return client.entities;

  if (isRealPersistedClient(client)) {
    warnSampleLeakBlocked(client, 'getClientEntities');
    return [];
  }

  if (client?.isSample) {
    return getSampleClientEntities(client);
  }

  return [];
}

export function getDefaultEntityId(client) {
  if (client?.selectedEntityId) return client.selectedEntityId;
  return getClientEntities(client)[0]?.id || '';
}

export function getEntityById(client, entityId) {
  const entities = getClientEntities(client);
  if (!entities.length) return null;
  if (!entityId) return entities[0] || null;
  return entities.find((e) => e.id === entityId) || entities[0] || null;
}

export function buildDefaultApplicantSelections(entity) {
  if (!entity?.owners?.length) return {};
  const primary = entity.owners.find((o) => o.isPrimary) || entity.owners[0];
  const selections = {};
  entity.owners.forEach((owner) => {
    const selected = owner.id === primary.id;
    selections[owner.id] = {
      selected,
      roleOnApplication: owner.isPrimary ? 'Primary Applicant' : 'Co-applicant',
      ownershipPercentUsed: owner.ownershipPercent ?? '',
    };
  });
  return selections;
}

export function applicantsFromSelections(entity, selections) {
  return Object.entries(selections)
    .filter(([, v]) => v.selected)
    .map(([ownerId, v]) => ({
      ownerId,
      roleOnApplication: v.roleOnApplication,
      ownershipPercentUsed: Number(v.ownershipPercentUsed) || 0,
    }));
}

export function applicantOwnerIdsFromSelections(selections) {
  return Object.entries(selections).filter(([, v]) => v.selected).map(([id]) => id);
}

export function ownershipTotalFromSelections(selections) {
  return Object.values(selections)
    .filter((v) => v.selected)
    .reduce((sum, v) => sum + (Number(v.ownershipPercentUsed) || 0), 0);
}

export function primaryOwnerFromSelections(entity, selections) {
  const selectedIds = applicantOwnerIdsFromSelections(selections);
  const owners = entity?.owners?.filter((o) => selectedIds.includes(o.id)) || [];
  return owners.find((o) => o.isPrimary) || owners[0] || entity?.owners?.find((o) => o.isPrimary) || entity?.owners?.[0];
}

export function maskSsn(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length < 4) return '***-**-****';
  return `***-**-${digits.slice(-4)}`;
}

export function maskEin(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length < 4) return '**-*******';
  return `**-***${digits.slice(-4)}`;
}

export function emptyOwnerForm() {
  return {
    name: '',
    role: 'Primary Owner',
    ownershipPercent: '100',
    ssn: '',
    dob: '',
    employer: '',
    income: '',
    personalAddress: '',
    isPrimary: true,
  };
}

export function emptyEntityForm(client) {
  const owner = emptyOwnerForm();
  owner.name = client?.name || '';
  return {
    label: '',
    entityType: 'LLC',
    businessLegalName: client?.businessName || '',
    dbaName: '',
    ein: '',
    businessStartDate: '',
    businessAddress: '',
    status: client?.status || 'Draft',
    owners: [owner],
  };
}

export function ownershipTotal(owners) {
  return (owners || []).reduce((sum, owner) => sum + (Number(owner.ownershipPercent) || 0), 0);
}

export function buildEntityFromForm(form, client) {
  const entityId = `entity_${Date.now()}`;
  let owners = (form.owners || []).filter((o) => o.name?.trim());

  if (form.entityType === 'Personal Profile' && owners.length === 0) {
    owners = [{
      name: client?.name || 'Primary owner',
      role: 'Primary Owner',
      ownershipPercent: '100',
      ssn: '',
      dob: '',
      employer: '',
      income: '',
      personalAddress: '',
      isPrimary: true,
    }];
  }

  const normalizedOwners = owners.map((owner, index) => ({
    id: `owner_${entityId}_${index}`,
    name: owner.name.trim(),
    role: owner.role || 'Primary Owner',
    ownershipPercent: Number(owner.ownershipPercent) || 0,
    ssnMasked: maskSsn(owner.ssn),
    dob: owner.dob || '',
    personalAddress: owner.personalAddress || '',
    income: owner.income ? Number(String(owner.income).replace(/[^0-9.]/g, '')) || owner.income : '',
    employer: owner.employer || '',
    isPrimary: Boolean(owner.isPrimary) || (index === 0 && !owners.some((o) => o.isPrimary)),
  }));

  if (normalizedOwners.length && !normalizedOwners.some((o) => o.isPrimary)) {
    normalizedOwners[0].isPrimary = true;
  }

  return {
    id: entityId,
    label: form.label.trim(),
    entityType: form.entityType,
    businessLegalName: form.businessLegalName?.trim() || form.label.trim(),
    dbaName: form.dbaName?.trim() || '',
    einMasked: maskEin(form.ein),
    businessStartDate: form.businessStartDate || '',
    businessAddress: form.businessAddress?.trim() || '',
    status: form.status || 'Draft',
    owners: normalizedOwners,
  };
}

export function appendEntityToClient(client, entity) {
  const currentEntities = Array.isArray(client?.entities) && client.entities.length
    ? client.entities
    : (client?.isSample ? getSampleClientEntities(client) : []);
  return {
    ...client,
    entities: [...currentEntities, entity],
    selectedEntityId: entity.id,
  };
}
